import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectedProductList from "./SelectedProductList";
import VariationTypeSelect from "./VariationTypeSelect";
import ConfirmationModal from "../../common/ConfirmationModal";
import { EditVariationProps } from "../../../interfaces/productVariationInterface";

const EditVariation = (props: EditVariationProps) => {
    const {
        currentProduct,
        showSnackbar,
        previousProducts,
        selectedProducts,
        setSelectedProducts,
        setIsSaveEnabled,
        productVariationPayload,
        setProductVariationPayload,
        showSkeleton,
        validator,
        fieldErrors,
        setFieldErrors,
        isEditMode,
        variations,
        setVariations,
        initialVariationTypes,
        checkedVariationType,
        setCheckedVariationType,
        setEditModeInitialRender,
        editModeInitialRender,
        completedDataSet,
        setCompletedDataSet
    } = props;
    
    const { t } = useTranslation();
    const [variationToDelete, setVariationToDelete] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deletedVariationTypes, setDeletedVariationTypes] = useState<string[]>([]);

    // Memoize complex calculations
    const isAllDeleted = useMemo(() => 
        productVariationPayload.productVariations.every(item => item.postingStatus === 9),
    [productVariationPayload.productVariations]
    );

    // Convert to useCallback to prevent recreation on each render
    const deleteCustomVariation = useCallback(() => {

        validator.current.fields = {};
        validator.current.errorMessages = {};
        if (variationToDelete === null) return;
        
        // Get the variation name before removing it
        const variationName = variations[variationToDelete]?.name;
        
        // Remove from variations array
        setVariations(prev => prev.filter((_, i) => i !== variationToDelete));
        
        // Remove from checkedVariationType if it was checked
        if (variations[variationToDelete]?.checked) {
            setCheckedVariationType(prev => 
                prev.filter(type => type !== variationName)
            );
            
            // Also update the product variation payload to remove this variation
            setProductVariationPayload(prevPayload => ({
                ...prevPayload,
                productVariations: prevPayload.productVariations.map(product => ({
                    ...product,
                    variations: product.variations.filter(
                        variation => variation.variationType !== variationName
                    ),
                })),
            }));
        }
        
        // Add to deletedVariationTypes to prevent re-adding
        if (variationName && variations[variationToDelete]?.isCustom) {
            setDeletedVariationTypes(prev => [...prev, variationName]);
        }
        
        // Extra: Remove any state variationType (old names) from payload
        setProductVariationPayload(prevPayload => ({
            ...prevPayload,
            productVariations: prevPayload.productVariations.map(product => ({
                ...product,
                variations: product.variations.filter(
                    variation => variations.some(v => v.name === variation.variationType)
                ),
            })),
        }));
        
        setModalOpen(false);
        setVariationToDelete(null);
    }, [variationToDelete, variations, setVariations, setCheckedVariationType, setProductVariationPayload]);

    useEffect(() => {
        if (selectedProducts?.length > 0 && isAllDeleted && !currentProduct) {
            setVariations(initialVariationTypes);
        }
    }, [selectedProducts, isAllDeleted, initialVariationTypes, setVariations]);



    const showVariationSelecthandler = () => {
        const isAllDeleted = productVariationPayload.productVariations.every((item) => item.postingStatus === 9);

        if(currentProduct && isAllDeleted){
            return false;
        }

        if(currentProduct && currentProduct?.variationTypes.length ===0) {
            return true;
        }
        else if (isAllDeleted){
            if (selectedProducts && selectedProducts.length > 0){
                return false;
            }
        }
        else if(selectedProducts && selectedProducts.length > 0){
            return true;
        }
    };

    const selectedProductListProps = {
        selectedProducts,
        setSelectedProducts,
        showSnackbar,
        checkedVariationType,
        setCheckedVariationType,
        productVariationPayload,
        setProductVariationPayload,
        previousProducts,
        showSkeleton,
        validator,
        fieldErrors,
        setFieldErrors,
        variations,
        setVariations,
        initialVariationTypes,
        isEditMode,
        completedDataSet,
        setCompletedDataSet,
    };

    const variationTypeSelectProps = {
        previousProducts,
        variations,
        setVariations,
        setIsSaveEnabled,
        setProductVariationPayload,
        productVariationPayload,
        setCheckedVariationType,
        checkedVariationType,
        setModalOpen,
        setVariationToDelete,
        deletedVariationTypes,
        showSnackbar,
        isEditMode,
        setEditModeInitialRender,
        editModeInitialRender,
        fieldErrors,
        setFieldErrors,
        completedDataSet,
        setCompletedDataSet
    };

    const confirmationModalProps = {
        open: modalOpen,
        onConfirm: deleteCustomVariation,
        onCancel: () => setModalOpen(false),
        title: "",
        message: t("product_variation_list.confirm_message_variation_type"),
        confirmText: t("product_variation_list.yes"),
        cancelText: t("product_variation_list.no")
    };

    return (
        <>
            <Grid container p={3} spacing={5}>
                <Grid item xs={12} sm={12} md={8} lg={!showVariationSelecthandler() ? 12 : 9}>
                    <SelectedProductList {...selectedProductListProps} />
                </Grid>
                
                {showVariationSelecthandler() && (
                    <Grid item xs={12} sm={12} md={4} lg={3}>
                        <VariationTypeSelect {...variationTypeSelectProps} mainValidator={validator}/>
                    </Grid>
                )}
            </Grid>
            
            <ConfirmationModal {...confirmationModalProps} />
        </>
    );
};

export default React.memo(EditVariation);